import type { HoppCollection } from "@hoppscotch/data"
import type { TeamCollection } from "~/helpers/teams/TeamCollection"

export function personalRequestRoot(
  roots: HoppCollection[],
  path: string,
  requestRefID?: string
): HoppCollection | undefined {
  // Prefer stable request identity: folder positions can change after sorting.
  const contains = (collection: HoppCollection): boolean =>
    collection.requests.some(
      (request) =>
        ("_ref_id" in request && request._ref_id === requestRefID) ||
        ("id" in request && request.id === requestRefID)
    ) || collection.folders.some(contains)
  const atPath = roots[Number(path.split("/")[0])]
  if (requestRefID) {
    const matches = roots.filter(contains)
    // Imported copies can share request refs. Use their current collection path
    // to disambiguate, while unique refs still survive collection reordering.
    return matches.length === 1
      ? matches[0]
      : matches.find((root) => root === atPath)
  }
  return atPath
}

export function personalCollectionRoot(roots: HoppCollection[], id: string) {
  const contains = (collection: HoppCollection): boolean =>
    collection._ref_id === id ||
    collection.id === id ||
    collection.folders.some(contains)
  return roots.find(contains)
}

export function teamRequestRoot(
  roots: TeamCollection[],
  path: string,
  requestID: string
): TeamCollection | undefined {
  const collectionID = path.split("/")[0]
  const contains = (collection: TeamCollection): boolean =>
    collection.id === collectionID ||
    !!collection.requests?.some((request) => request.id === requestID) ||
    !!collection.children?.some(contains)
  return roots.find(contains)
}

export function collectionEnvironmentIds(data: unknown): string[] {
  if (!data || typeof data !== "object") return []
  const ids = (data as { environmentIds?: unknown }).environmentIds
  return Array.isArray(ids) && ids.every((id) => typeof id === "string")
    ? ids
    : []
}

let reconcileSelection: (() => void) | undefined
let reconciling = false

export function registerCollectionEnvironmentReconciler(reconcile: () => void) {
  reconcileSelection = reconcile
  return () => {
    if (reconcileSelection === reconcile) reconcileSelection = undefined
  }
}

export function ensureCollectionEnvironmentSelection() {
  if (reconciling) return
  reconciling = true
  try {
    reconcileSelection?.()
  } finally {
    reconciling = false
  }
}

/** Stores IDs only; environment values and credentials never enter this cache. */
export class CollectionEnvironmentSelection {
  private previousKey: string | undefined
  private previousSelection: string | null = null
  private awaitingRestore = false

  constructor(public remembered: Record<string, string | null>) {}

  reconcile(
    defaultKey: string,
    collectionKey: string | undefined,
    allowed: string[] | null,
    available: string[],
    selected: string | null,
    pending = false
  ): string | null {
    if (pending) {
      this.awaitingRestore = true
      return null
    }
    // Selection belongs to the collection even when its picker is unrestricted.
    const key = collectionKey ?? defaultKey
    const filtered = !!allowed?.length
    const valid = (id: string | null) =>
      id !== null &&
      available.includes(id) &&
      (!filtered || allowed!.includes(id))
    const changed = key !== this.previousKey
    let next = selected

    if (
      !changed &&
      this.awaitingRestore &&
      selected !== this.previousSelection &&
      valid(selected)
    ) {
      next = selected
      this.awaitingRestore = false
      this.remembered[key] = next
    } else if (changed || this.awaitingRestore) {
      // A new saved collection never inherits the previous collection's target.
      // Unsaved requests retain a separate workspace-level selection.
      const saved =
        this.remembered[key] ??
        (!collectionKey && this.previousKey === undefined ? selected : null)
      next = valid(saved) ? saved : null
      this.awaitingRestore =
        saved !== null &&
        !available.includes(saved) &&
        (!filtered || allowed!.includes(saved))
      if (!this.awaitingRestore) this.remembered[key] = next
    } else if (
      selected !== this.previousSelection ||
      (selected !== null && !valid(selected))
    ) {
      next = valid(selected) ? selected : null
      this.remembered[key] = next
    }

    this.previousKey = key
    this.previousSelection = next
    return next
  }
}
